import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SelfServiceDashboardService } from '../../shared/self-service-dashboard.service';

/** Data passed in by the opener (dashboard-builder's shareDashboard()). */
export interface ShareDashboardDialogData {
  /** Full read-only URL handed to viewers. */
  shareLink: string;
  /** Slug of the `/dynamic-dashboard/:slug` read-only route. */
  shareSlug: string;
  /** Display name of the dashboard (used as `dashboard_id` in the grant payload).
   *  Optional — falls back to the name inside the fetched layout / the slug. */
  dashboardName?: string;
  /** Full layout JSON to grant. Optional — the dialog fetches it by slug when omitted. */
  layoutJson?: Record<string, unknown>;
}

/**
 * Read-only "Share dashboard" dialog. Shows the link a creator hands to viewers
 * (who can view but not edit), with copy + open-preview actions. Also lets the
 * creator grant a specific user access by email — this inserts a copy of the
 * dashboard layout under that user's id so it shows up in their dashboard list.
 * Extracted from DashboardBuilderComponent so the dialog UI is self-contained.
 */
@Component({
  selector: 'app-share-dashboard-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './share-dashboard-dialog.component.html',
  styleUrl: './share-dashboard-dialog.component.scss',
})
export class ShareDashboardDialogComponent {
  shareLinkCopied = false;

  // ── Grant-to-user state ────────────────────────────────────────────────────
  grantEmail = '';
  granting = false;
  grantStatus: 'idle' | 'success' | 'error' = 'idle';
  grantMessage = '';

  constructor(
    private readonly dialogRef: MatDialogRef<ShareDashboardDialogComponent>,
    private readonly router: Router,
    private readonly dashboardService: SelfServiceDashboardService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ShareDashboardDialogData,
  ) {}

  /** Copy the share URL to the clipboard, with a graceful fallback for browsers
   *  that block the async Clipboard API (e.g. non-secure contexts). */
  async copyShareLink(): Promise<void> {
    const shareUrl = this.data.shareLink;
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        this.shareLinkCopied = true;
        return;
      }
    } catch {
      // Fall through to the manual prompt below.
    }
    window.prompt('Copy this read-only share link:', shareUrl);
    this.shareLinkCopied = true;
  }

  /** Grant a specific user access to this dashboard by inserting the layout under
   *  their email in `dashboard_layouts`. The dashboard then appears in their own
   *  dashboard list the next time they open the app. */
  async grantAccess(): Promise<void> {
    const email = this.grantEmail.trim();
    this.grantStatus = 'idle';
    this.grantMessage = '';

    if (!this.isValidEmail(email)) {
      this.grantStatus = 'error';
      this.grantMessage = 'Enter a valid email address.';
      return;
    }

    this.granting = true;
    try {
      // Use the layout passed by the opener when available; otherwise pull it by
      // slug (the same email-independent lookup shared links rely on).
      const layout =
        this.data.layoutJson ??
        (await firstValueFrom(this.dashboardService.getPublishedLayoutBySlug(this.data.shareSlug)));

      if (!layout) {
        this.grantStatus = 'error';
        this.grantMessage = 'Could not load this dashboard’s layout. Please try again.';
        return;
      }

      const dashboardName =
        this.data.dashboardName ||
        String((layout as Record<string, unknown>)?.['name'] ?? this.data.shareSlug);

      const ok = await firstValueFrom(
        this.dashboardService.shareLayoutWithUser(layout, email, dashboardName),
      );

      if (ok) {
        this.grantStatus = 'success';
        this.grantMessage = `Shared with ${email}. It will appear in their dashboard list.`;
        this.grantEmail = '';
      } else {
        this.grantStatus = 'error';
        this.grantMessage = 'Unable to share the dashboard. Please try again.';
      }
    } catch {
      this.grantStatus = 'error';
      this.grantMessage = 'Unable to share the dashboard. Please try again.';
    } finally {
      this.granting = false;
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /** Open the read-only preview the recipient will see, in a new tab. The
   *  `shared=1` flag forces the recipient's read-only experience (Edit Layout
   *  only, no Back-to-Builder/Share) EVEN when the creator opens it — otherwise
   *  the ownership check would render the owner toolbar in the preview. */
  openSharePreview(): void {
    if (!this.data.shareSlug) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/', 'dynamic-dashboard', this.data.shareSlug],
        { queryParams: { shared: 1 } },
      ),
    );
    this.dialogRef.close();
    window.open(url, '_blank', 'noopener');
  }

  close(): void {
    this.dialogRef.close();
  }
}
